import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useWebsiteSettings } from '@/hooks/useWebsiteSettings';

interface SitemapRouteProps {
  type?: 'main' | 'games' | 'categories' | 'blog' | 'pages' | 'index';
}

const SitemapRoute: React.FC<SitemapRouteProps> = ({ type = 'index' }) => {
  const [xmlContent, setXmlContent] = useState<string>('');
  const { settings } = useWebsiteSettings();
  
  const { data: games } = useQuery({
    queryKey: ['sitemap-games-all'],
    queryFn: async () => {
      console.log('Fetching all games for sitemap...');
      
      // First get total count
      const { count } = await supabase
        .from('games')
        .select('*', { count: 'exact', head: true })
        .eq('hidden', false);

      console.log(`Total games: ${count}`);

      if (!count || count === 0) {
        return [];
      }

      // Fetch all games in batches
      const batchSize = 1000;
      const totalBatches = Math.ceil(count / batchSize);
      let allGames: any[] = [];

      for (let i = 0; i < totalBatches; i++) {
        const start = i * batchSize;
        const end = start + batchSize - 1;
        
        console.log(`Fetching batch ${i + 1}/${totalBatches}`);
        
        const { data: games, error } = await supabase
          .from('games')
          .select('id, title, updated_at, featured, trending, created_at, thumbnail')
          .eq('hidden', false)
          .order('updated_at', { ascending: false })
          .range(start, end);

        if (error) {
          console.error(`Error fetching games batch ${i + 1}:`, error);
          continue;
        }

        if (games && games.length > 0) {
          allGames = allGames.concat(games);
        }
      }

      console.log(`Successfully loaded ${allGames.length} games`);
      return allGames;
    },
    enabled: type === 'games' || type === 'index',
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const { data: categories } = useQuery({
    queryKey: ['sitemap-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('slug, name, updated_at')
        .order('updated_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: type === 'categories' || type === 'index',
  });

  const { data: blogPosts } = useQuery({
    queryKey: ['sitemap-blog'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('slug, title, updated_at, published_at, featured_image_url')
        .eq('status', 'published')
        .order('updated_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: type === 'blog' || type === 'index',
  });

  const { data: pages } = useQuery({
    queryKey: ['sitemap-pages'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pages')
        .select('slug, title, updated_at')
        .eq('published', true)
        .order('updated_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: type === 'pages' || type === 'index',
  });

  const createGameSlug = (title: string): string => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim()
      .replace(/^-+|-+$/g, '');
  };

  const generateSitemapXML = (urls: Array<{
    loc: string;
    lastmod: string;
    changefreq: string;
    priority: string;
    image?: string;
  }>): string => {
    let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
    xml += '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9" xmlns:image="http://www.google.com/schemas/sitemap-image/1.1">\n';
    
    urls.forEach(url => {
      xml += '  <url>\n';
      xml += `    <loc>${url.loc}</loc>\n`;
      xml += `    <lastmod>${url.lastmod}</lastmod>\n`;
      xml += `    <changefreq>${url.changefreq}</changefreq>\n`;
      xml += `    <priority>${url.priority}</priority>\n`;
      
      if (url.image) {
        xml += '    <image:image>\n';
        xml += `      <image:loc>${url.image}</image:loc>\n`;
        xml += '    </image:image>\n';
      }
      
      xml += '  </url>\n';
    });
    
    xml += '</urlset>';
    return xml;
  };

  const generateSitemapIndex = (baseUrl: string): string => {
    const sitemaps = [
      'sitemap-main.xml',
      'sitemap-games.xml',
      'sitemap-categories.xml',
      'sitemap-blog.xml',
      'sitemap-pages.xml'
    ];

    let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
    xml += '<sitemapindex xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n';
    
    sitemaps.forEach(sitemap => {
      xml += '  <sitemap>\n';
      xml += `    <loc>${baseUrl}/${sitemap}</loc>\n`;
      xml += `    <lastmod>${new Date().toISOString()}</lastmod>\n`;
      xml += '  </sitemap>\n';
    });
    
    xml += '</sitemapindex>';
    return xml;
  };

  useEffect(() => {
    if (!settings) return;

    const baseUrl = settings.site_url || window.location.origin;
    let xml = '';

    switch (type) {
      case 'main':
        const mainUrls = [
          {
            loc: baseUrl,
            lastmod: new Date().toISOString(),
            changefreq: 'daily',
            priority: '1.0'
          },
          {
            loc: `${baseUrl}/categories`,
            lastmod: new Date().toISOString(),
            changefreq: 'daily',
            priority: '0.8'
          },
          {
            loc: `${baseUrl}/blog`,
            lastmod: new Date().toISOString(),
            changefreq: 'daily',
            priority: '0.8'
          },
          {
            loc: `${baseUrl}/search`,
            lastmod: new Date().toISOString(),
            changefreq: 'weekly',
            priority: '0.6'
          }
        ];
        xml = generateSitemapXML(mainUrls);
        break;

      case 'games':
        if (games && games.length > 0) {
          console.log(`Generating sitemap for ${games.length} games`);
          const gameUrls = games.map(game => {
            const gameAge = new Date().getTime() - new Date(game.created_at).getTime();
            const daysSinceCreated = gameAge / (1000 * 60 * 60 * 24);
            
            let changefreq = 'monthly';
            if (daysSinceCreated < 7) {
              changefreq = 'daily';
            } else if (daysSinceCreated < 30) {
              changefreq = 'weekly';
            }
            
            const gameSlug = createGameSlug(game.title);
            
            return {
              loc: `${baseUrl}/game/${gameSlug}`,
              lastmod: game.updated_at,
              changefreq,
              priority: game.featured ? '0.9' : game.trending ? '0.8' : '0.7',
              image: game.thumbnail
            };
          });
          xml = generateSitemapXML(gameUrls);
        }
        break;

      case 'categories':
        if (categories) {
          const categoryUrls = categories.map(category => ({
            loc: `${baseUrl}/category/${category.slug}`,
            lastmod: category.updated_at,
            changefreq: 'daily',
            priority: '0.8'
          }));
          xml = generateSitemapXML(categoryUrls);
        }
        break;

      case 'blog':
        if (blogPosts) {
          const blogUrls = blogPosts.map(post => {
            const postAge = new Date().getTime() - new Date(post.published_at || post.updated_at).getTime();
            const daysSincePublished = postAge / (1000 * 60 * 60 * 24);
            
            const changefreq = daysSincePublished < 30 ? 'weekly' : 'monthly';
            
            return {
              loc: `${baseUrl}/blog/${post.slug}`,
              lastmod: post.updated_at,
              changefreq,
              priority: daysSincePublished < 7 ? '0.8' : '0.7',
              image: post.featured_image_url
            };
          });
          xml = generateSitemapXML(blogUrls);
        }
        break;

      case 'pages':
        const pageUrls: Array<{
          loc: string;
          lastmod: string;
          changefreq: string;
          priority: string;
        }> = [];

        if (pages) {
          pageUrls.push(...pages.map(page => ({
            loc: `${baseUrl}/page/${page.slug}`,
            lastmod: page.updated_at,
            changefreq: 'monthly',
            priority: '0.6'
          })));
        }

        // Add static pages
        const staticPages = [
          {
            loc: `${baseUrl}/privacy`,
            lastmod: new Date().toISOString(),
            changefreq: 'yearly',
            priority: '0.3'
          },
          {
            loc: `${baseUrl}/terms`,
            lastmod: new Date().toISOString(),
            changefreq: 'yearly',
            priority: '0.3'
          },
          {
            loc: `${baseUrl}/about`,
            lastmod: new Date().toISOString(),
            changefreq: 'monthly',
            priority: '0.5'
          },
          {
            loc: `${baseUrl}/contact`,
            lastmod: new Date().toISOString(),
            changefreq: 'monthly',
            priority: '0.5'
          }
        ];

        pageUrls.push(...staticPages);
        xml = generateSitemapXML(pageUrls);
        break;

      case 'index':
      default:
        xml = generateSitemapIndex(baseUrl);
        break;
    }

    setXmlContent(xml);
  }, [type, games, categories, blogPosts, pages, settings]);

  useEffect(() => {
    if (xmlContent) {
      // Replace the entire document with XML content
      const newDoc = document.implementation.createHTMLDocument();
      newDoc.documentElement.innerHTML = `<head><meta http-equiv="Content-Type" content="application/xml; charset=utf-8"></head><body><pre style="word-wrap: break-word; white-space: pre-wrap; font-family: monospace; margin: 0; padding: 16px;">${xmlContent}</pre></body>`;
      
      // Replace current document
      document.open();
      document.write('<!DOCTYPE html><html><head><meta http-equiv="Content-Type" content="application/xml; charset=utf-8"><title>Sitemap</title></head><body><pre style="word-wrap: break-word; white-space: pre-wrap; font-family: monospace; margin: 0; padding: 16px; background: #1a1a1a; color: #e1e1e1; line-height: 1.4;">' + xmlContent.replace(/</g, '&lt;').replace(/>/g, '&gt;') + '</pre></body></html>');
      document.close();
    }
  }, [xmlContent]);

  // Show loading while generating XML
  if (!xmlContent) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        fontFamily: 'system-ui, sans-serif',
        background: '#1a1a1a',
        color: '#e1e1e1'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #333',
            borderTop: '4px solid #3498db',
            borderRadius: '50%',
            animation: 'spin 2s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ color: '#ccc', margin: 0 }}>Generating sitemap...</p>
          <style dangerouslySetInnerHTML={{
            __html: `
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `
          }} />
        </div>
      </div>
    );
  }

  return null;
};

export default SitemapRoute;
